import React, { useEffect, useState } from "react";
import { Storage } from "aws-amplify";
import { getImageType } from "../../../../core/utils/general/strings";
import { multiImageZipKey } from "../../../../core/utils/dataloading/multiImageZipKey";
import {
  Row,
  Col,
  Card,
  Tooltip,
  Popconfirm,
  Typography,
  Form,
  Modal,
  Input,
  message,
} from "antd";
import {
  UploadOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import BulkUploadResultsModal from "./creatives/BulkUploadResultModal";
import MediaUploadModal from "../../Creatives/components/MediaUploadModal";
import "./../../Creatives/library/creativestyle.css";
import Button from "../../../shared/globalStyling/Button";
import SearchBar from "../../Creatives/library/SearchBar";
import { useLibraryCreativesContext } from "../../../../core/components/creatives/library/useLibraryCreativesContext";
import AddVideosModal from "../../Creatives/components/AddVideosModal";
import { uploadToS3Storage } from "../../Creatives/utils/mediaUtils";
const { Title } = Typography;

const VideoTab = ({
  videoCreativeIds,
  onCreateAdgroup,
  onDeleteMedias,
  refetchVideoMedias,
  searchText,
  handleSearch,
  setVideoCreativeIds,
  videoCreativeKeys,
  setVideoCreativeKeys,
  bulkCreateOrgMedia,
  clearAllSelections,
}) => {
  const {
    createOrgMedia,
    orgVideoMedias,
    updateAdGroupCreatives,
    adGroupCreatives,
  } = useLibraryCreativesContext() || {};
  const [formAdGroup] = Form.useForm();
  const [uploadModal, setUploadModal] = useState(false);
  const [adGroupModal, setAdGroupModal] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [assigning, setAssigning] = useState(false);

  const [assignAdGroupModalVisible, setAssignAdGroupModalVisible] = useState(
    false
  );
  const [selectedAdGroupIds, setSelectedAdGroupIds] = useState([]);
  const [selectedAdUnits, setSelectedAdUnits] = useState([]);
  const [downloadLoading, setDownloadLoading] = useState(false);

  const [bulkUploadLoading, setBulkUploadLoading] = useState(false);
  const [uploadComplete, setUploadComplete] = useState(false);
  const [uploadResult, setUploadResults] = useState([]);
  const [
    showUploadResultsTableModal,
    setShowUploadResultsTableModal,
  ] = useState(false);

  // Clear upload results when upload modal is opened
  useEffect(() => {
    if (uploadModal) {
      setUploadResults([]);
    }
  }, [uploadModal]);

  // Execute query to refetch videos after upload/delete/createadgroup
  const refetchVideos = async () => {
    refetchVideoMedias({
      variables: {
        type: 'VIDEO',
      },
    });
  };

  // Handle uploads from MediaUploadModal
  const handleMediaUpload = async (mediaData, mode) => {
    if (mode === "single") {
      // Handle single upload
      setUploadLoading(true);
      try {
        const media = mediaData[0];
        await createOrgMedia({
          variables: {
            mediatype: "VIDEO",
            key: media.key,
            url: media.url,
            width: media.width,
            height: media.height,
            name: media.name,
            size: media.size,
            fileType: media.fileType,
          },
        });

        // Refetch data before showing success and closing modal
        await refetchVideos();

        // Only show success message after data is actually saved and refetched
        message.success('Video Creative saved successfully!');
        setUploadModal(false);
      } catch (error) {
        message.error(`File upload failed: ${error.message}`);
      } finally {
        setUploadLoading(false);
      }
    } else if (mode === "bulk") {
      // Handle bulk upload
      setBulkUploadLoading(true);
      setUploadComplete(false);
      setUploadResults([]);
      setUploadModal(false);
      setShowUploadResultsTableModal(true);

      const results = [];

      try {
        for (const mediaFile of mediaData) {
          const { file, dimensions, name } = mediaFile;

          try {
            // Upload to S3
            const response = await uploadToS3Storage(file, "video/mp4");

            // Prepare result for UI
            const result = {
              name,
              dimensions: `${dimensions.width}x${dimensions.height}`,
              fileSize: file.size,
              duration: 0,
              status: true,
            };

            setUploadResults(prev => [...prev, result]);

            // Prepare for bulk create
            results.push({
              type: "VIDEO",
              key: response.key,
              url: response.url,
              width: dimensions.width,
              height: dimensions.height,
              name,
              size: file.size,
              fileType: "MP4",
            });
          } catch (uploadError) {
            console.error(`Failed to upload ${file.name}:`, uploadError);

            const failedResult = {
              name,
              dimensions: `${dimensions.width}x${dimensions.height}`,
              fileSize: file.size,
              duration: 0,
              status: false,
              error: uploadError.message?.includes('multipart')
                ? "Large file upload failed"
                : "Upload failed",
            };

            setUploadResults(prev => [...prev, failedResult]);
          }
        }

        // Bulk create all videos
        await bulkCreateOrgMedia(results, [], true);
        await refetchVideos();

        setUploadComplete(true);
        message.success(`Successfully uploaded ${results.length} video(s)`);

        // Close modal immediately after success
        setShowUploadResultsTableModal(false);
      } catch (error) {
        message.error("Bulk upload failed");
      } finally {
        setBulkUploadLoading(false);
      }
    }
  };


  const handleAssignAdUnits = async () => {
    setAssigning(true);
    try {
      // Use the proper assignment approach - connect media to existing ad groups
      await Promise.all(
        selectedAdGroupIds.map(adGroupId =>
          updateAdGroupCreatives({
            variables: {
              id: adGroupId,
              data: {
                medias: {
                  connect: selectedAdUnits.map(unit => ({ id: unit.id })),
                },
              },
            },
          })
        )
      );
      message.success("Videos assigned to ad group(s)!");
      setAssignAdGroupModalVisible(false);
      setSelectedAdGroupIds([]);
      setSelectedAdUnits([]);

      // Clear local selections immediately
      setVideoCreativeIds([]);

      // Clear all selections through parent state management
      if (clearAllSelections) {
        clearAllSelections();
      }

      await refetchVideos();
    } catch (error) {
      message.error("Failed to assign videos to ad group(s)");
    } finally {
      setAssigning(false);
    }
  };
  const validateCreateVideoAdGroupName = (_, value) => {
    if (!value) return Promise.resolve();

    const lowerValue = value.toLowerCase().trim();

    // Check for duplicates against all existing ad groups (case-insensitive)
    const isDuplicate = adGroupCreatives?.some(
      group => group.name?.toLowerCase().trim() === lowerValue
    );

    if (isDuplicate) {
      return Promise.reject(new Error("Ad group name already exists"));
    }

    return Promise.resolve();
  };

  // Function to handle creating an ad group with selected video creatives
  const handleCreateAdGroup = async values => {
    formAdGroup.resetFields();

    const remakeIds = videoCreativeIds.map(idVal => ({ id: idVal }));
    const adGroupName = values.name;

    try {
      // Use the parent's onCreateAdgroup function instead of calling GraphQL directly
      await onCreateAdgroup(adGroupName, remakeIds);

      setAssignAdGroupModalVisible(false); // Close the assignment modal too
      setAdGroupModal(false); // Close the create ad group modal after creation
      formAdGroup.resetFields();

      // Note: selection clearing is handled by parent LibraryCreativesViewImproved
      setSelectedAdUnits([]);
      setSelectedAdGroupIds([]);
    } catch (error) {
      message.error("Failed to create Video Ad Group");
    }
  };

  // Function to handle deleting video creatives
  const handleDelete = async videoCreativeIds => {
    await onDeleteMedias(videoCreativeIds);
    await refetchVideos();
    if (typeof setVideoCreativeIds === "function") {
      setVideoCreativeIds([]);
    }
  };

  const handleDownloadAdUnits = async () => {
    if (!videoCreativeKeys || videoCreativeKeys.length === 0) {
      return message.warning("Please select video ad units to download");
    }

    setDownloadLoading(true);
    try {
      const results = await multiImageZipKey(
        `Video Creatives (${videoCreativeKeys.length} videos)`,
        videoCreativeKeys
      );

      // Check for complete failure
      if (results.success === false) {
        const failedCount = Array.isArray(results.failedDownloads)
          ? results.failedDownloads.length
          : results.failedDownloads;
        message.error(
          `Failed to download ${failedCount} file(s). Files may be deleted or inaccessible.`
        );
      }
      // Check for partial failure (some succeeded, some failed)
      else if (
        (Array.isArray(results.failedDownloads) && results.failedDownloads.length > 0) ||
        (typeof results.failedDownloads === 'number' && results.failedDownloads > 0) ||
        results.failedKeys?.length > 0
      ) {
        const successCount = results.successfulDownloads || 0;
        const failedCount = Array.isArray(results.failedDownloads)
          ? results.failedDownloads.length
          : results.failedDownloads || results.failedKeys?.length || 0;
        message.warning(
          `Partial download: ${successCount} video(s) downloaded successfully, ${failedCount} failed. Failed files may be deleted or inaccessible.`
        );
      }
      // Complete success
      else {
        const successCount = results.successfulDownloads || videoCreativeKeys.length;
        message.success(
          `Successfully downloaded ${successCount} video creative(s)`
        );
      }
    } catch (error) {
      message.error("Failed to download video creatives");
    } finally {
      setDownloadLoading(false);
    }
  };

  return (
    <>
      <BulkUploadResultsModal
        visible={showUploadResultsTableModal}
        uploadResults={uploadResult}
        uploadInProgress={bulkUploadLoading}
        uploadComplete={uploadComplete}
        onCancel={() => {
          setShowUploadResultsTableModal(false);
        }}
      />

      {/* Shared Media Upload Modal */}
      <MediaUploadModal
        visible={uploadModal}
        onCancel={() => setUploadModal(false)}
        onUpload={handleMediaUpload}
        mediaType="VIDEO"
        title="Upload Video(s)"
        loading={uploadLoading}
        existingCreatives={orgVideoMedias?.medias || []}
      />
      <AddVideosModal
        setAdGroupModalVisible={setAssignAdGroupModalVisible}
        adGroupModalVisible={assignAdGroupModalVisible}
        setAdGroupModal={setAdGroupModal}
        ids={videoCreativeIds}
        orgMedias={orgVideoMedias ? orgVideoMedias.medias : []}
        selectedAdUnits={selectedAdUnits}
        adGroupCreatives={adGroupCreatives}
        selectedAdGroupIds={selectedAdGroupIds}
        setSelectedAdGroupIds={setSelectedAdGroupIds}
        handleAssignAdUnits={handleAssignAdUnits}
        assigning={assigning}
      />
      <Modal
        title={`Create Video Ad Group - ${videoCreativeIds.length} Creative(s) Selected`}
        open={adGroupModal}
        centered
        footer={null}
        onCancel={() => {
          setAdGroupModal(false);
          formAdGroup.resetFields();
        }}
      >
        <Form layout="vertical" onFinish={handleCreateAdGroup} form={formAdGroup}>
          <Row type="flex" justify="space-around">
            <Col span={24}>
              <Form.Item
                label="Name (Max 255 characters)"
                name="name"
                rules={[
                  { required: true, message: "Please enter an ad group name" },
                  { max: 255, message: "Ad group name must not exceed 255 characters" },
                  {
                    validator: validateCreateVideoAdGroupName,
                  },
                ]}
              >
                <Input placeholder="Name of Video Ad Group" maxLength={255} showCount />
              </Form.Item>
            </Col>
          </Row>
          <Row type="flex" justify="space-around" align="middle">
            <Col span={24}>
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <Button htmlType="submit" type="primary">
                  Create
                </Button>
              </div>
            </Col>
          </Row>
        </Form>
      </Modal>
      <Row>
        <Col xs={24} xl={24}>
          <Card bodyStyle={{ padding: 10 }}>
            <Row align="middle" className="ant-row-justify-space-between">
              <Col>
                <Title level={4}>Video Ad Units</Title>
              </Col>
              <Col>
                <SearchBar searchText={searchText} onSearch={handleSearch} />
              </Col>
            </Row>
            <Row align="middle" className="ant-row-justify-space-between">
              <Col xs={24} sm={16} md={16} lg={14}>
                <Row>
                  <Popconfirm
                    placement="topLeft"
                    title={`Are you sure you want to delete the selected creatives? This action cannot be undone.`}
                    onConfirm={() => {
                      handleDelete(videoCreativeIds);
                    }}
                    okText="Delete"
                    cancelText="Cancel"
                  >
                    <Tooltip
                      placement="bottom"
                      title={`Delete selected creatives`}
                    >
                      <Button
                        type="default"
                        disabled={!videoCreativeIds || videoCreativeIds.length === 0}
                      >
                        Delete
                      </Button>
                    </Tooltip>
                  </Popconfirm>
                  <Button
                    type="default"
                    disabled={
                      !videoCreativeIds || videoCreativeIds.length === 0 || downloadLoading
                    }
                    loading={downloadLoading}
                    onClick={handleDownloadAdUnits}
                  >
                    Download Ad Unit
                  </Button>
                  <Button
                    type="default"
                    disabled={
                      !videoCreativeIds || videoCreativeIds.length === 0
                    }
                    onClick={() => {
                      setSelectedAdUnits(
                        (orgVideoMedias
                          ? orgVideoMedias.medias
                          : []
                        ).filter(item => videoCreativeIds.includes(item.id))
                      );
                      setAssignAdGroupModalVisible(true);
                    }}
                  >
                    Assign to Ad Groups
                  </Button>
                </Row>
              </Col>
              <Col xs={24} sm={8} md={8} lg={10} style={{ textAlign: "right" }}>
                <Button
                  type="primary"
                  icon={<UploadOutlined />}
                  onClick={() => setUploadModal(true)}
                  disabled={false}
                >
                  Upload Video(s)
                </Button>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default VideoTab;
